// One AI employee's page (/owner-board/team/:key, owner-only, 2026-11-04).
//
// Magnus: "each employee has its own kpi and they think of new ideas to improve it … right now I
// need to open the conversation and go to that page. I can't just zoom in directly." So this page
// holds everything about one employee without going anywhere else: the decisions waiting for him
// (answer in place), the KPI scoreboard (value vs target, trend, change vs last period), their
// improvement ideas per KPI (做這個 / 先不要 in one tap), today's full report and earlier ones,
// the conversation itself with a reply box, open work and their permission record. The avatar rail
// on top jumps straight to any other employee.
//
// Data: GET /api/agent-threads/team/:key (+ /team for the rail). Replies and idea taps go into the
// employee's latest daily thread (POST /:id/messages), which queues their next turn.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import api from '../services/api';
import { ApprovalCard, RichText, ago, clockTime, errMessage } from '../components/agent/agentShared';
import { useTeam } from '../components/agent/EmployeeTeam';
import {
  AMBER,
  Avatar,
  BAD,
  GOOD,
  IDEA_OPEN,
  IDEA_STATUS,
  Idea,
  ScoreKpi,
  Snapshot,
  Sparkline,
  StatusPill,
  Task,
  assignTask,
  TeamApproval,
  TeamThread,
  deltaTone,
  sendToEmployee,
  statusOf,
  taskNeedsYou,
  withUnit,
} from '../components/agent/employeeShared';
import EmployeeTasks, { TaskPill } from '../components/agent/EmployeeTasks';

interface PageThread extends TeamThread {
  title: string;
  report: { content: string; created_at: string } | null;
}
interface PageMessage {
  id: number;
  role: 'user' | 'assistant' | 'activity' | 'approval' | 'system';
  content: string;
  meta: any;
  created_at: string;
}
interface PageData {
  employee: { key: string; name: string; workstream: string; snapshot: Snapshot | null };
  threads: PageThread[];
  messages: PageMessage[];
  approvals: TeamApproval[];
  tasks?: Task[];
}

const Section: React.FC<{ title: React.ReactNode; right?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  right,
  children,
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1f5b4c' }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);

const KpiCard: React.FC<{ k: ScoreKpi; ideas: number }> = ({ k, ideas }) => {
  const [open, setOpen] = useState(false);
  const tone = deltaTone(k);
  const border = k.state === 'hit' ? GOOD : k.state === 'miss' ? '#EF9F27' : 'divider';
  return (
    <Paper
      variant="outlined"
      onClick={() => setOpen((v) => !v)}
      sx={{ p: 1.25, borderRadius: 2.5, cursor: 'pointer', minWidth: 0, borderLeft: `4px solid`, borderLeftColor: border }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ minWidth: 0 }}>{k.label}</Typography>
        {k.state !== 'none' && (
          <Box component="span" sx={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, px: 0.75, borderRadius: 99,
            bgcolor: k.state === 'hit' ? '#EAF3DE' : '#FAEEDA', color: k.state === 'hit' ? GOOD : AMBER }}>
            {k.state === 'hit' ? '達標' : `差 ${withUnit(Math.abs((k.target ?? 0) - (k.value ?? 0)), k.unit)}`}
          </Box>
        )}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.25 }}>{withUnit(k.value, k.unit)}</Typography>
      {k.prev != null && k.value != null && (
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: tone > 0 ? GOOD : tone < 0 ? BAD : 'text.disabled' }}>
          {k.value > k.prev ? '▲' : k.value < k.prev ? '▼' : '＝'}{' '}
          <Box component="span" sx={{ fontWeight: 400 }}>前期 {withUnit(k.prev, k.unit)}</Box>
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11.5 }}>
        {k.target == null ? '還沒定目標' : `目標 ${k.better === 'down' ? '≤' : '≥'} ${withUnit(k.target, k.unit)}${k.draft ? '（草定）' : ''}`}
      </Typography>
      <Box sx={{ mt: 0.75 }}>
        <Sparkline k={k} height={30} />
        {k.series.length < 2 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
            {k.value == null ? '還沒量' : '明天起有趨勢線'}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: 11, color: ideas ? '#0C447C' : 'text.disabled' }}>
        {ideas ? `${ideas} 個點子在跑` : k.state === 'miss' ? '還沒有點子' : ' '}
      </Typography>
      <Collapse in={open}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
          怎麼算：{k.how}
          {k.detail ? `\n最新：${k.detail}` : ''}
          {k.at ? `（${ago(k.at)}前）` : ''}
        </Typography>
      </Collapse>
    </Paper>
  );
};

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  proposed: { bg: '#E6F1FB', fg: '#0C447C' },
  doing: { bg: '#FAEEDA', fg: AMBER },
  testing: { bg: '#FAEEDA', fg: AMBER },
  shipped: { bg: '#EAF3DE', fg: GOOD },
  won: { bg: '#EAF3DE', fg: GOOD },
  lost: { bg: '#FCEBEB', fg: BAD },
  dropped: { bg: '#F1EFE8', fg: '#5F5E5A' },
};

// What Magnus already did with a still-'proposed' idea, so the card shows it at once instead of
// looking untouched until the employee's next snapshot (2026-11-05: "when I click 做這個 nothing
// changed" — he tapped #18 three times). A task in the lane wins; else his latest message about it.
type IdeaHandled = { kind: 'task'; task: Task } | { kind: 'said'; go: boolean; at: string };
const ideaTag = (id: number) => `點子 #${id}「`;
const ideaTaskText = (key: string, i: Idea) =>
  [
    `${ideaTag(i.id)}${i.title}」：做這個`,
    i.expect ? `預估：${i.expect}` : '',
    `先把點子改成在做（employee.py idea set ${key} ${i.id} --status doing），做完用 --note / --result 寫回點子。需要我批的部分放卡片給我。`,
  ]
    .filter(Boolean)
    .join('\n');

const IdeaCard: React.FC<{
  idea: Idea;
  kpi?: ScoreKpi;
  busy: boolean;
  handled: IdeaHandled | null;
  onGo: () => void;
  onDrop: () => void;
}> = ({ idea, kpi, busy, handled, onGo, onDrop }) => {
  const t = STATUS_TONE[idea.status] || STATUS_TONE.dropped;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
        <Box component="span" sx={{ bgcolor: t.bg, color: t.fg, fontSize: 11, fontWeight: 700, px: 1, py: 0.25, borderRadius: 99 }}>
          {IDEA_STATUS[idea.status] || idea.status}
        </Box>
        {kpi && <Chip size="small" variant="outlined" label={kpi.label} sx={{ height: 20, fontSize: 11 }} />}
        {idea.effort && (
          <Typography variant="caption" color="text.secondary">工作量 {idea.effort}</Typography>
        )}
        {idea.check_on && <Typography variant="caption" color="text.secondary">· {idea.check_on.slice(5)} 看結果</Typography>}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.45 }}>{idea.title}</Typography>
      {idea.expect && (
        <Typography variant="body2" sx={{ fontSize: 13, color: '#0C447C', mt: 0.25 }}>預估：{idea.expect}</Typography>
      )}
      {idea.why && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mt: 0.25, lineHeight: 1.5 }}>{idea.why}</Typography>
      )}
      {(idea.note || idea.result) && (
        <Typography variant="body2" sx={{ fontSize: 12.5, mt: 0.5, color: 'text.secondary' }}>
          {idea.result ? `結果：${idea.result}` : `進度：${idea.note}`}
        </Typography>
      )}
      {idea.status === 'proposed' && handled?.kind === 'task' && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
          <TaskPill t={handled.task} />
          <Typography variant="body2" sx={{ fontSize: 12.5, color: 'text.secondary', minWidth: 0 }} noWrap>
            已派工 · {handled.task.state === 'running' || handled.task.state === 'queued'
              ? handled.task.status_line || '他在做'
              : handled.task.evidence || '看上面「任務」'}
          </Typography>
        </Box>
      )}
      {idea.status === 'proposed' && handled?.kind === 'said' && (
        <Typography variant="body2" sx={{ mt: 1, fontSize: 12.5, fontWeight: 700, color: handled.go ? '#0C447C' : 'text.secondary' }}>
          {handled.go ? `已交代 · ${ago(handled.at)}前，他做了會把點子改成「在做」` : `已說先不要 · ${ago(handled.at)}前`}
        </Typography>
      )}
      {idea.status === 'proposed' && !handled && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button size="small" variant="contained" disableElevation disabled={busy} onClick={onGo}>
            做這個
          </Button>
          <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={onDrop}>
            先不要
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

// Long replies (earlier reports) fold to a few lines so the page stays readable on a phone.
const AssistantBubble: React.FC<{ m: PageMessage }> = ({ m }) => {
  const long = m.content.split('\n').length > 8 || m.content.length > 420;
  const [open, setOpen] = useState(!long);
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '94%' }}>
      <Box sx={{ bgcolor: 'grey.100', px: 1.5, py: 1.25, borderRadius: 3, borderBottomLeftRadius: 6,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: open ? 'none' : 150, overflow: 'hidden' }}>
        <Typography variant="body2" sx={{ lineHeight: 1.75 }}><RichText text={m.content} /></Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {clockTime(m.created_at)}
        {long && (
          <Box component="span" role="button" onClick={() => setOpen((v) => !v)}
            sx={{ ml: 1, color: 'primary.main', cursor: 'pointer', fontWeight: 700 }}>
            {open ? '收起' : '展開'}
          </Box>
        )}
      </Typography>
    </Box>
  );
};

const Message: React.FC<{ m: PageMessage; running: boolean }> = ({ m, running }) => {
  if (m.role === 'user') {
    const label = m.meta?.display || m.content;
    if (m.meta?.kind === 'decision') {
      return <Box sx={{ textAlign: 'center' }}><Chip size="small" variant="outlined" label={label} /></Box>;
    }
    // The daily brief is the long scheduled prompt — show it as a one-line marker.
    if (/（每日排程）/.test(label)) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
          {clockTime(m.created_at)} 上班
        </Typography>
      );
    }
    return (
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 1.5, py: 1, borderRadius: 3,
          borderBottomRightRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          <Typography variant="body2">{label}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.25 }}>
          {m.meta?.pending ? (running ? '下一輪送出' : '等待送出') : clockTime(m.created_at)}
        </Typography>
      </Box>
    );
  }
  if (m.role === 'assistant') return <AssistantBubble m={m} />;
  if (m.role === 'activity') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
        🔧 {m.meta?.steps ?? 0} 個步驟
      </Typography>
    );
  }
  if (m.role === 'approval') {
    const st = m.meta?.state;
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Chip size="small" variant="outlined" sx={{ maxWidth: '100%' }}
          label={`${st === 'open' ? '等你決定' : st === 'approved' ? '✅ 已核准' : st === 'answered' ? `已回答：${m.meta?.answer || ''}` : '已處理'} · ${m.meta?.summary || m.content}`} />
      </Box>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>{m.content}</Typography>
  );
};

const railWait = (x: { approvals: unknown[]; tasks?: Task[] }) =>
  x.approvals.length + (x.tasks || []).filter((t) => !t.archived && taskNeedsYou(t)).length;

const STAGE = { autonomous: '自主', intern: '實習', floor: '只有你能做' } as const;

const EmployeePage: React.FC = () => {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { employees } = useTeam(60000);
  const [data, setData] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState('');
  const [openReport, setOpenReport] = useState<number | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [showAllMsgs, setShowAllMsgs] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Taps not yet visible in the reloaded data (the card must change on the tap itself).
  const [tapped, setTapped] = useState<Record<number, { go: boolean; at: string }>>({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/agent-threads/team/${key}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(errMessage(err));
    }
  }, [key]);

  useEffect(() => {
    setData(null);
    setOpenReport(null);
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const thread = data?.threads?.[0] || null;
  const act = async (fn: () => Promise<unknown>, done?: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (done) setFlash(done);
      await load();
      return true;
    } catch (err) {
      setError(errMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const say = (text: string, done = `已告訴${data?.employee.name || '他'}，他下一輪會處理`) =>
    act(() => sendToEmployee(key, thread, text), done);

  const s = data?.employee.snapshot || null;
  const board = useMemo(() => s?.scoreboard || [], [s]);
  const ideas = useMemo(() => s?.ideas || [], [s]);
  const kpiById = useMemo(() => Object.fromEntries(board.map((k) => [k.id, k])), [board]);
  const handledOf = (i: Idea): IdeaHandled | null => {
    const tag = ideaTag(i.id);
    const task = (data?.tasks || []).find((t) => (t.ask || '').startsWith(tag));
    if (task) return { kind: 'task', task };
    // messages come newest first, so this is his latest word on the idea
    const m = (data?.messages || []).find((x) => x.role === 'user' && String(x.meta?.display || x.content || '').startsWith(tag));
    if (m) return { kind: 'said', go: !String(m.meta?.display || m.content).includes('先不要'), at: m.created_at };
    const tap = tapped[i.id];
    return tap ? { kind: 'said', ...tap } : null;
  };
  const ideaProps = (i: Idea) => ({
    handled: handledOf(i),
    onGo: () => {
      setTapped((x) => ({ ...x, [i.id]: { go: true, at: new Date().toISOString() } }));
      act(() => assignTask(key, ideaTaskText(key, i)), `已派工給${data?.employee.name || '他'}，在「任務」看進度`).then(
        (ok) => !ok && setTapped(({ [i.id]: _, ...rest }) => rest)
      );
    },
    onDrop: () => {
      setTapped((x) => ({ ...x, [i.id]: { go: false, at: new Date().toISOString() } }));
      say(`${ideaTag(i.id)}${i.title}」：先不要，把狀態改成不做。`, '好，這個點子先不做').then(
        (ok) => !ok && setTapped(({ [i.id]: _, ...rest }) => rest)
      );
    },
  });
  const openIdeasList = ideas.filter((i) => IDEA_OPEN.includes(i.status));
  const closedIdeas = ideas.filter((i) => !IDEA_OPEN.includes(i.status));

  if (!data) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  const e = data.employee;
  const st = statusOf({ thread, approvals: data.approvals, snapshot: s, tasks: data.tasks });
  const running = thread?.status === 'running' || thread?.status === 'queued';
  const hit = board.filter((k) => k.state === 'hit').length;
  const measured = board.filter((k) => k.target != null && k.value != null).length;
  const todayReport = thread?.report;
  const earlier = data.threads.slice(1).filter((t) => t.report);
  // Ad-hoc numbers outside the scoreboard — only those logged with a short Chinese label.
  const extra = (s?.kpis || []).filter((x) => x.label && x.label.length <= 16 && x.label !== x.detail);
  // The latest reply is the report already shown in full above; don't print it twice.
  const convo = data.messages.filter((m) => !(m.role === 'assistant' && todayReport && m.content === todayReport.content));
  const shown = showAllMsgs ? convo : convo.slice(-12);
  const tasks = data.tasks || [];
  const openTasks = tasks.filter((t) => !t.archived);
  const needYou = openTasks.filter(taskNeedsYou).length;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', pb: 4 }}>
      {/* ── rail: every employee one tap away ── */}
      {!!employees?.length && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <IconButton aria-label="回我的團隊" onClick={() => navigate('/owner-board#team')} sx={{ flexShrink: 0 }}>
            <ArrowBackRoundedIcon />
          </IconButton>
          {employees.map((x) => (
            <Box key={x.key} role="link" aria-label={`切到${x.name}`} onClick={() => navigate(`/owner-board/team/${x.key}`)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', flexShrink: 0, width: 48,
                position: 'relative', opacity: x.key === key ? 1 : 0.7 }}>
              <Box sx={{ borderRadius: '50%', outline: x.key === key ? '2px solid #1f5b4c' : 'none', outlineOffset: 2 }}>
                <Avatar name={x.name} workstream={x.workstream} size={34} />
              </Box>
              {railWait(x) > 0 && (
                <Box sx={{ position: 'absolute', top: -3, right: 2, bgcolor: '#EF9F27', color: '#fff', fontSize: 9.5, fontWeight: 700,
                  borderRadius: 99, minWidth: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {railWait(x)}
                </Box>
              )}
              <Typography variant="caption" noWrap sx={{ fontSize: 10.5, maxWidth: 48, fontWeight: x.key === key ? 700 : 400 }}>
                {x.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── who ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Avatar name={e.name} workstream={e.workstream} size={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.25 }}>{e.name}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {s ? `${s.role} · 每天 ${s.hour}:00 上班` : e.workstream}
          </Typography>
          {running && thread?.status_line && (
            <Typography variant="caption" color="primary" noWrap component="div">{thread.status_line}</Typography>
          )}
        </Box>
        <StatusPill st={st} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {/* bottom toast: a tap far down the page must still visibly land */}
      <Snackbar open={!!flash} autoHideDuration={4000} onClose={() => setFlash(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setFlash(null)} sx={{ width: '100%' }}>{flash}</Alert>
      </Snackbar>

      {/* ── decisions waiting for him ── */}
      {data.approvals.length > 0 && (
        <Section title={<Box component="span" sx={{ color: AMBER }}>等你決定 · {data.approvals.length}</Box>}>
          <Stack spacing={1.25}>
            {data.approvals.map((a) => (
              <ApprovalCard
                key={a.id}
                meta={a.meta}
                busy={busy}
                header={<Typography variant="caption" color="text.secondary">{ago(a.created_at)}前</Typography>}
                onZoom={setZoom}
                onApprove={(note) => act(() => api.post(`/api/agent-threads/${a.thread_id}/decide`, { approval_id: a.id, action: 'approve', note }), '已核准')}
                onAnswer={(answer, note) => act(() => api.post(`/api/agent-threads/${a.thread_id}/decide`, { approval_id: a.id, action: 'answer', answer, note }), '已回答')}
                onRevise={(note) => act(() => api.post(`/api/agent-threads/${a.thread_id}/messages`, { text: note }), '已告訴他要改的地方')}
                onDone={() => act(() => api.post(`/api/agent-threads/${a.thread_id}/decide`, { approval_id: a.id, action: 'done' }))}
              />
            ))}
          </Stack>
        </Section>
      )}

      {/* ── task lane: assign one thing, watch it run, see the evidence ── */}
      <Section
        title={<>任務 · {openTasks.length}{needYou > 0 && <Box component="span" sx={{ color: AMBER }}>（{needYou} 件等你）</Box>}</>}
      >
        <EmployeeTasks
          empKey={key}
          name={e.name}
          tasks={tasks}
          onChanged={(m) => { if (m) setFlash(m); load(); }}
          onError={setError}
        />
      </Section>

      {/* ── scoreboard ── */}
      {board.length > 0 && (
        <Section
          title="成績單"
          right={measured > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: hit === measured ? GOOD : 'text.secondary' }}>
              達標 {hit} / {measured}
            </Typography>
          )}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
            {board.map((k) => (
              <KpiCard key={k.id} k={k} ideas={openIdeasList.filter((i) => i.kpi === k.id).length} />
            ))}
          </Box>
          {extra.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              其他數字：{extra.map((x) => `${x.label} ${withUnit(x.value, '')}`).join(' · ')}
            </Typography>
          )}
        </Section>
      )}

      {/* ── improvement ideas ── */}
      <Section
        title={<><LightbulbOutlinedIcon sx={{ fontSize: 17, verticalAlign: '-3px', mr: 0.5 }} />改善點子 · {openIdeasList.length}</>}
        right={
          <Button size="small" disabled={busy}
            onClick={() => say('看你的成績單：每個沒達標的 KPI 各想 2 個新點子（要具體、有數字根據），用 employee.py idea add 記下來，回報給我。', `已叫${e.name}想點子`)}>
            叫他多想點子
          </Button>
        }
      >
        {openIdeasList.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            還沒有點子。從下一次上班起，每個沒達標的 KPI 都會至少有一個點子。
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            {[...openIdeasList]
              .sort((a, b) => IDEA_OPEN.indexOf(b.status) - IDEA_OPEN.indexOf(a.status))
              .map((i) => (
                <IdeaCard key={i.id} idea={i} kpi={kpiById[i.kpi]} busy={busy} {...ideaProps(i)} />
              ))}
          </Box>
        )}
        {closedIdeas.length > 0 && (
          <>
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowClosed((v) => !v)}>
              {showClosed ? '收起' : `做完的點子 ${closedIdeas.length}`}
            </Button>
            <Collapse in={showClosed}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                {closedIdeas.map((i) => (
                  <IdeaCard key={i.id} idea={i} kpi={kpiById[i.kpi]} busy={busy} {...ideaProps(i)} />
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Section>

      {/* ── today's report, in full ── */}
      <Section title={todayReport ? `日報 · ${ago(todayReport.created_at)}前` : '日報'}>
        {todayReport ? (
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
            <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.75, fontSize: 14 }}>
              <RichText text={todayReport.content} />
            </Typography>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">還沒有日報。</Typography>
        )}
        {earlier.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {earlier.map((t) => (
              <Box key={t.id}>
                <Button size="small" color="inherit" onClick={() => setOpenReport(openReport === t.id ? null : t.id)}
                  sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}>
                  {openReport === t.id ? '▾' : '▸'} {t.created_at.slice(5, 10).replace('-', '/')} 的日報
                </Button>
                <Collapse in={openReport === t.id} unmountOnExit>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                    <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7 }}>
                      <RichText text={t.report!.content} />
                    </Typography>
                  </Paper>
                </Collapse>
              </Box>
            ))}
          </Stack>
        )}
      </Section>

      {/* ── the conversation, inline ── */}
      <Section title="對話">
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
          <Stack spacing={1.25} sx={{ mb: 1.25 }}>
            {convo.length > shown.length && (
              <Button size="small" onClick={() => setShowAllMsgs(true)}>看更早的 {convo.length - shown.length} 則</Button>
            )}
            {convo.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {data.messages.length ? '日報在上面。有事就直接跟他說。' : `還沒有對話。傳一句話給${e.name}，他會馬上上班處理。`}
              </Typography>
            )}
            {shown.map((m) => <Message key={m.id} m={m} running={running} />)}
            {todayReport && convo.length < data.messages.length && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                ↑ 最新回覆就是上面的日報
              </Typography>
            )}
          </Stack>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={6}
              size="small"
              placeholder={`跟${e.name}說…（例：這個目標改成 2.2%、先處理退款）`}
              value={reply}
              onChange={(ev) => setReply(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey) && reply.trim()) {
                  say(reply.trim()).then((ok) => ok && setReply(''));
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <IconButton color="primary" aria-label="送出" disabled={busy || !reply.trim()}
              onClick={() => say(reply.trim()).then((ok) => ok && setReply(''))}>
              {busy ? <CircularProgress size={20} /> : <SendRoundedIcon />}
            </IconButton>
          </Box>
          {!running && (st.tone === 'off' || st.tone === 'done' || st.tone === 'fail') && (
            <Button size="small" sx={{ mt: 1 }} disabled={busy}
              onClick={() => act(() => api.post('/api/agent-threads', { preset_key: `emp_${key}` }), `${e.name}上班了`)}>
              現在叫他上班（跑完整一輪）
            </Button>
          )}
        </Paper>
      </Section>

      {/* ── open work ── */}
      {(!!s?.actions_today?.length || !!s?.in_progress?.length) && (
        <Section title="工作">
          {!!s?.actions_today?.length && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">今天自己做完的</Typography>
              {s.actions_today.map((x, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: 13 }}>· {x}</Typography>
              ))}
            </Box>
          )}
          {!!s?.in_progress?.length && (
            <Box>
              <Typography variant="caption" color="text.secondary">手上的事</Typography>
              {s.in_progress.map((x, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>· {x}</Typography>
              ))}
            </Box>
          )}
        </Section>
      )}

      {/* ── permission record ── */}
      {!!s?.classes?.length && (
        <Section title="工作權限" right={<Typography variant="caption" color="text.secondary">✓ 你直接批 · ~ 改了才批 · ✗ 不要</Typography>}>
          <Stack spacing={0.5}>
            {s.classes.map((c) => (
              <Box key={c.cls} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 13 }}>
                <Box component="span" sx={{ minWidth: 0 }}>{c.label}</Box>
                <Box component="span" sx={{ whiteSpace: 'nowrap', fontWeight: c.stage === 'autonomous' ? 700 : 400,
                  color: c.stage === 'autonomous' ? GOOD : c.stage === 'floor' ? 'text.disabled' : 'text.secondary' }}>
                  {STAGE[c.stage]}
                  {c.stage === 'intern' && ` ${c.record || ''}${c.need != null ? ` · 還差 ${c.need} 次` : ''}`}
                  {s.ready.includes(c.cls) && ' · 可授權'}
                </Box>
              </Box>
            ))}
          </Stack>
        </Section>
      )}

      {s?.pushed_at && (
        <Typography variant="caption" color="text.disabled">資料更新 {ago(s.pushed_at)}前</Typography>
      )}

      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="lg">
        {zoom && <Box component="img" src={zoom} alt="" onClick={() => setZoom(null)} sx={{ maxWidth: '100%', display: 'block' }} />}
      </Dialog>
    </Box>
  );
};

export default EmployeePage;
